
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import { Typography } from '@mui/material';
import {
    CollaboratingGroupContext,
    CrfForm,
    FormBreadcrumbs,
    GroupedField,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    ToggleButtonGroup
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { useContext } from 'react';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { ValidateOn } from '@ngt/forms-core';
import { AppSettings } from '../../../AppSettings';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IRegistrationEligibilityChecklistFormProps { }

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

const RegistrationEligibilityChecklistForm: React.FunctionComponent<IRegistrationEligibilityChecklistFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings?.debug}
            >
                <Typography
                    variant="h2"
                    sx={{px: 2, py: 3}}
                >
                    Pre-registration Inclusion Criteria &ndash; Must be YES or patient ineligible
                </Typography>

                <GroupedField
                    propertyPath="breastCancerEligibility"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="tenYearsFollowUp"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="performedBreastImaging"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="willingToHaveSurgery"
                    component={ToggleButtonGroup}
                />

                <Typography
                    variant="h2"
                    sx={{px: 2, py: 3}}
                >
                    Pre-registration Exclusion Criteria &ndash; Must be NO or patient ineligible
                </Typography>

                <GroupedField
                    propertyPath="tripleNegativeBreastCancer"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="previousDCIS"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="planToHaveMastectomy"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="lviReported"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="multifocalBreastCancer"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="distantMetastasis"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="bilateralBreastCancer"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="geneMutation"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="contraindicationToMRI"
                    component={ToggleButtonGroup}
                />
                <GroupedField
                    propertyPath="concurrentIllness"
                    component={ToggleButtonGroup}
                />

            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default RegistrationEligibilityChecklistForm;
